/**
 * Custom Analytics Hook for PostHog
 * Provides type-safe, strategic event tracking with automatic batching and error handling
 */

import { useCallback, useEffect, useState } from 'react';
import { safeCapture, identifyUser, resetAnalytics, isPostHogLoaded, getPostHog } from '../lib/analytics';
import { ANALYTICS_EVENTS, BUTTON_CONFIGS, ButtonPriority } from '../lib/analyticsTypes';
import type { 
  UseAnalyticsReturn, 
  ButtonClickProperties, 
  ErrorProperties, 
  BaseAnalyticsProperties 
} from '../lib/analyticsTypes';

// Event batching for performance
class EventBatcher {
  private events: Array<{ name: string; properties: any }> = [];
  private timer: NodeJS.Timeout | null = null;
  private readonly batchSize: number = 10;
  private readonly batchTimeout: number = 2000; // 2 seconds

  addEvent(name: string, properties: any): void {
    this.events.push({ name, properties });

    if (this.events.length >= this.batchSize) {
      this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => this.flush(), this.batchTimeout);
    }
  }

  flush(): void {
    if (this.events.length === 0) return;

    // Send all batched events
    this.events.forEach(({ name, properties }) => {
      safeCapture(name, properties);
    });

    this.events = [];
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  destroy(): void {
    this.flush();
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

// Singleton batcher instance
const eventBatcher = new EventBatcher();

export const useAnalytics = (): UseAnalyticsReturn => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Check PostHog loading status
    const checkLoaded = () => {
      setIsLoaded(isPostHogLoaded());
    };

    checkLoaded();
    
    // Poll for PostHog to be ready
    const interval = setInterval(checkLoaded, 100);
    
    // Cleanup after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Enhanced event tracking with common properties
  const trackEvent = useCallback((eventName: string, properties?: Record<string, any>) => {
    const enhancedProperties: BaseAnalyticsProperties = {
      ...properties,
      page_path: window.location.pathname,
      timestamp: new Date().toISOString(),
      user_agent_mobile: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent)
    };

    safeCapture(eventName, enhancedProperties);
  }, []);

  // Strategic button click tracking
  const trackButtonClick = useCallback((
    buttonName: string, 
    context: string = 'unknown',
    additionalProps?: Record<string, any>
  ) => {
    // Check if this button should be tracked
    const buttonConfig = BUTTON_CONFIGS[buttonName];
    
    if (buttonConfig?.priority === ButtonPriority.EXCLUDED) {
      console.log(`Button ${buttonName} excluded from tracking`);
      return;
    }

    // Get element position if available
    const getElementPosition = (name: string): string | undefined => {
      try {
        const elements = document.querySelectorAll(`[data-button="${name}"], [data-testid="${name}"], [aria-label="${name}"]`);
        if (elements.length > 0) {
          const element = elements[0];
          const rect = element.getBoundingClientRect();
          return `x:${Math.round(rect.left)},y:${Math.round(rect.top)}`;
        }
      } catch (error) {
        console.warn('Could not determine element position:', error);
      }
      return undefined;
    };

    const properties: ButtonClickProperties = {
      button_name: buttonName,
      button_type: buttonConfig?.category === 'conversion' ? 'primary' : 'secondary',
      context: context,
      element_position: getElementPosition(buttonName),
      page_section: context,
      page_path: window.location.pathname,
      timestamp: new Date().toISOString(),
      user_agent_mobile: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent),
      ...additionalProps
    };

    // Use different events for different priority levels
    if (buttonConfig?.priority === ButtonPriority.HIGH) {
      trackEvent(ANALYTICS_EVENTS.CTA_CLICKED, properties);
    } else {
      trackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, properties);
    }
  }, [trackEvent]);

  // Error tracking with context
  const trackError = useCallback((error: Error, context?: string) => {
    const properties: ErrorProperties = {
      error_message: error.message,
      error_type: error.name || 'Error',
      component_name: context,
      stack_trace: error.stack?.substring(0, 1000), // Truncate stack trace
      page_path: window.location.pathname,
      timestamp: new Date().toISOString(),
      user_agent_mobile: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent)
    };

    trackEvent(ANALYTICS_EVENTS.ERROR_ENCOUNTERED, properties);
  }, [trackEvent]);

  // Manual page view tracking (for special cases)
  const trackPageView = useCallback((pageName?: string, additionalProps?: Record<string, any>) => {
    const properties = {
      page_name: pageName || document.title || window.location.pathname,
      page_path: window.location.pathname,
      page_url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString(),
      ...additionalProps
    };

    trackEvent('$pageview', properties);
  }, [trackEvent]);

  // User identification
  const identify = useCallback((userId: string, userProperties?: Record<string, any>) => {
    identifyUser(userId, userProperties);
  }, []);

  // Reset analytics (useful for logout)
  const reset = useCallback(() => {
    resetAnalytics();
  }, []);

  return {
    trackEvent,
    trackButtonClick,
    trackError,
    trackPageView,
    identify,
    reset,
    isLoaded
  };
};

// Specialized hooks for common use cases

// Document upload tracking
export const useDocumentAnalytics = () => {
  const { trackEvent } = useAnalytics();

  const trackDocumentUpload = useCallback((
    fileType: string,
    fileSizeMB: number,
    uploadMethod: 'drag_drop' | 'click_upload' | 'paste'
  ) => {
    trackEvent(ANALYTICS_EVENTS.DOCUMENT_UPLOADED, {
      document_type: fileType,
      file_size_mb: fileSizeMB,
      file_extension: fileType.toLowerCase(),
      upload_method: uploadMethod,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackDocumentAnalyzed = useCallback((documentId: string, processingTimeMs?: number) => {
    trackEvent(ANALYTICS_EVENTS.DOCUMENT_ANALYZED, {
      document_id: documentId,
      processing_time_ms: processingTimeMs,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  return { trackDocumentUpload, trackDocumentAnalyzed };
};

// Chat interaction tracking
export const useChatAnalytics = () => {
  const { trackEvent } = useAnalytics();

  const trackChatMessage = useCallback((
    messageLength: number,
    conversationId: string,
    hasDocumentContext: boolean = false,
    messageType: 'question' | 'follow_up' | 'clarification' = 'question'
  ) => {
    trackEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_SENT, {
      message_length: messageLength,
      conversation_id: conversationId,
      has_document_context: hasDocumentContext,
      message_type: messageType,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackConversationStarted = useCallback((conversationId: string) => {
    trackEvent(ANALYTICS_EVENTS.CHAT_CONVERSATION_STARTED, {
      conversation_id: conversationId,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  return { trackChatMessage, trackConversationStarted };
};

// Subscription tracking
export const useSubscriptionAnalytics = () => {
  const { trackEvent } = useAnalytics();

  const trackSubscriptionStarted = useCallback((
    planType: 'free' | 'pro' | 'enterprise',
    billingCycle: 'monthly' | 'yearly',
    priceCents: number
  ) => {
    trackEvent(ANALYTICS_EVENTS.SUBSCRIPTION_STARTED, {
      plan_type: planType,
      billing_cycle: billingCycle,
      price_cents: priceCents,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackUpgradeClicked = useCallback((currentPlan: string, targetPlan: string) => {
    trackEvent(ANALYTICS_EVENTS.UPGRADE_BUTTON_CLICKED, {
      current_plan: currentPlan,
      target_plan: targetPlan,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  return { trackSubscriptionStarted, trackUpgradeClicked };
};

// Onboarding tracking
export const useOnboardingAnalytics = () => {
  const { trackEvent } = useAnalytics();

  const trackOnboardingCompleted = useCallback((stepsCompleted: number, totalSteps: number) => {
    trackEvent(ANALYTICS_EVENTS.USER_ONBOARDED, {
      steps_completed: stepsCompleted,
      total_steps: totalSteps,
      completion_rate: (stepsCompleted / totalSteps) * 100,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackFeatureDiscovered = useCallback((featureName: string, discoveryMethod: string) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE_DISCOVERED, {
      feature_name: featureName,
      discovery_method: discoveryMethod,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  return { trackOnboardingCompleted, trackFeatureDiscovered };
};

// Cleanup function for event batcher
export const cleanupAnalytics = (): void => {
  eventBatcher.destroy();
};